import {Routes} from '@angular/router';
import {authGuard} from "./guards/auth-guard/auth-guard";
import {rootRedirectGuard} from './guards/root-redirect/root-redirect';
import {environment} from '../environments/environment';

export const routes: Routes = [
  {
    path: '',
    canActivate: [rootRedirectGuard],
    loadComponent: () =>
      import('./pages/home/home.page').then(m => m.HomePage),
    pathMatch: 'full',
  },
  {
    path: 'home',
    loadComponent: () =>
      import('./pages/home/home.page').then(m => m.HomePage),
    canActivate: [authGuard],
    data: {
      nav: {
        exact: true,
        icon: 'home',
        label: 'nav.dashboard',
        order: 10,
        section: 'main',
      },
    },
  },
  {
    path: 'dashboard',
    loadChildren: () =>
      import('./pages/home/dashboard.routes').then(m => m.dashboardRoutes),
  },
  {
    path: 'customer',
    loadChildren: () =>
      import('./pages/customer/customer.routes').then(m => m.customerRoutes),
    data: {
      nav: {
        activePrefix: '/customer',
        activePrefixes: ['/user-group'],
        icon: 'customers',
        label: 'nav.customer_management',
        order: 20,
        route: '/customer/dashboard',
        section: 'main',
      },
    },
  },
  {
    path: 'location',
    loadChildren: () =>
      import('./pages/location/location.routes').then(m => m.locationRoutes),
    data: {
      nav: {
        activePrefix: '/location',
        icon: 'locations',
        label: 'nav.location_management',
        // Directly below the home dashboard entry (order 10) in the main section.
        order: 20,
        route: '/location/dashboard',
        section: 'main',
      },
    },
  },
  {
    path: 'module',
    loadChildren: () =>
      import('./pages/module/module.routes').then(m => m.moduleRoutes),
    data: {
      nav: {
        icon: 'modules',
        label: 'nav.modules',
        order: 30,
        route: '/module/modules',
        section: 'main',
      },
    },
  },
  {
    path: 'dashboard-templates',
    loadChildren: () =>
      import('./pages/dashboard-template/dashboard-template.routes').then(m => m.dashboardTemplateRoutes),
  },
  {
    path: 'user',
    loadChildren: () =>
      import('./pages/user/user.routes').then(m => m.userRoutes),
    data: {
      nav: {
        activePrefix: '/user',
        icon: 'user',
        label: 'nav.user_management',
        // In the admin section, directly below Kundenverwaltung (order 20).
        order: 30,
        route: '/user/dashboard',
        section: 'admin',
      },
    },
  },
  {
    path: 'options',
    loadChildren: () =>
      import('./pages/options/options.routes').then(m => m.optionsRoutes),
    data: {
      nav: {
        icon: 'options',
        label: 'nav.options',
        order: 10,
        route: '/options/widgets',
        section: 'account',
      },
    },
  },
  {
    // No nav entry: user groups are reached from a customer, never from the menu.
    path: 'user-group',
    loadChildren: () =>
      import('./pages/user-group/user-group.routes').then(m => m.userGroupRoutes),
  },
  {
    path: 'audit-log',
    loadChildren: () =>
      import('./pages/audit-log/audit-log.routes').then(m => m.auditLogRoutes),
    data: {
      nav: {
        icon: 'audit-log',
        label: 'nav.audit_log',
        order: 50,
        section: 'main',
      },
    },
  },
  {
    path: 'csv-imports',
    loadChildren: () => import('./pages/csv-import/csv-import.routes').then(m => m.csvImportRoutes),
  },
  {
    path: 'charts',
    loadComponent: () => import('./pages/TEMP/charts/charts.page').then(m => m.ChartsPage)
  },
  {
    path: '__chart-builder',
    canMatch: [() => !environment.production],
    loadComponent: () => import('./pages/TEMP/chart-builder/chart-builder.page').then(m => m.ChartBuilderPage),
    canActivate: [authGuard],
  },
  {
    path: '__energy-flow-preview',
    canMatch: [() => !environment.production && ['localhost', '127.0.0.1'].includes(window.location.hostname)],
    loadComponent: () => import('./pages/TEMP/energy-flow-preview.page').then(m => m.EnergyFlowPreviewPage),
  },
  {
    path: 'login',
    loadComponent: () => import('./pages/auth/login/login.page').then(m => m.LoginPage)
  },
  {
    path: 'register',
    loadComponent: () => import('./pages/auth/register/register.page').then(m => m.RegisterPage)
  },
  {
    path: 'auth',
    children: [
      {
        path: 'two-fa-setup',
        loadComponent: () => import('./pages/auth/two-fa-setup/two-fa-setup.page').then(m => m.TwoFaSetupPage)
      },
      {
        path: 'two-fa-confirm',
        loadComponent: () => import('./pages/auth/two-fa-confirm/two-fa-confirm.page').then(m => m.TwoFaConfirmPage)
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('./pages/auth/forgot-password/forgot-password.page').then(m => m.ForgotPasswordPage)
      },
    ]
  },
  {
    path: 'notifications',
    data: {
      nav: {
        icon: 'notifications',
        label: 'nav.notifications',
        order: 15,
        section: 'main',
      }
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/notifications/notifications.page').then( m => m.NotificationsPage)
      },
      {
        path: ':id',
        loadComponent: () => import('./pages/notifications/detail/detail.page').then( m => m.DetailPage)
      }
    ],
    canActivate: [authGuard]
  },
  {
    path: 'accept-invitation',
    loadComponent: () => import('./pages/auth/invitation/invitation.page').then(m => m.InvitationPage)
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./pages/auth/reset-password/reset-password.page').then(m => m.ResetPasswordPage)
  },
  {
    // Landing target of the backend's email-verification redirect. Verification already happened
    // server-side, so this only sends the user on to the login screen, where the verified state
    // is reflected.
    path: 'email-verified',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: 'error',
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/error/error.page').then( m => m.ErrorPage)
      },
      {
        path: ':code',
        loadComponent: () => import('./pages/error/error.page').then( m => m.ErrorPage)
      },
    ]
  },
  {
    path: 'reports-and-trends',
    loadChildren: () => import('./pages/reports-and-trends/trends.routes').then(m => m.trendsRoutes),
    data: {
      nav: {
        activePrefix: '/reports-and-trends',
        route: '/reports-and-trends/dashboard',
        icon: 'chart-line-up',
        label: 'nav.trends',
        order: 24,
        section: 'main',
      },
    },
  },
  {
    path: 'report-templates',
    loadChildren: () => import('./pages/reports-and-trends/reports-and-trends.routes').then(m => m.reportsAndTrendsRoutes),
    data: {
      nav: {
        activePrefix: '/report-templates',
        icon: 'chart-pie-simple',
        label: 'nav.reports',
        route: '/report-templates/dashboard',
        order: 25,
        section: 'main',
      },
    },
  },
  {
    path: 'reports',
    loadChildren: () => import('./pages/reports-and-trends/reports-and-trends.routes').then(m => m.reportRoutes),
  },
  {
    path: 'settings',
    loadComponent: () => import('./pages/settings/settings.page').then( m => m.SettingsPage),
    canActivate:[authGuard]
  },
  {
    path: '**',
    redirectTo: 'error/404'
  },

];
